import { ConfigurationTableModel201 } from '@energy-stacks/broker/feature-charging-station-management-data';
import { ConfigurationBooleanValueField } from '../../protocol-v201/configuration-tab/ConfigurationBooleanValueField';
import { Row } from '@tanstack/react-table';
import { Configuration21FormData } from './ConfigurationTable21';
import { Control, Controller } from 'react-hook-form';
import { ConfigurationIntegerValueField } from '../../protocol-v201/configuration-tab/ConfigurationIntegerValueField';
import { ConfigurationDecimalValueField } from '../../protocol-v201/configuration-tab/ConfigurationDecimalValueField';
import { ConfigurationDateTimeValueField } from '../../protocol-v201/configuration-tab/ConfigurationDateTimeValueField';
import { ConfigurationStringValueFieldV21 } from './ConfigurationStringValueFieldV21';
import { ConfigurationCommaSeparatedMultiSelectV21 } from './ConfigurationCommaSeparatedMultiSelectV21';
import { ConfigurationOptionListValueFieldV21 } from './ConfigurationOptionListValueFieldV21';
import { Box } from '@mui/material';
import { upperFirst } from 'lodash-es';

interface ConfigurationValueFieldProps {
  row: Row<ConfigurationTableModel201>;
  control: Control<Configuration21FormData, object>;
  index: number;
}

export const ConfigurationValueFieldV21: React.FC<
  ConfigurationValueFieldProps
> = ({ row, control, index }) => {
  const characteristics = row.original.variableCharacteristics;
  const dataType = characteristics?.dataType;
  const valuesList = characteristics?.valuesList;

  if (row.original.variableAttribute.mutability !== 'ReadOnly') {
    return (
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => {
          const handleValueChange = (newValue: string) => {
            onChange(newValue);
          };

          if (!characteristics) {
            return (
              <ConfigurationStringValueFieldV21
                handleValueChange={handleValueChange}
                controlerValue={typeof value === 'string' ? value : ''}
                maxLimit={0}
              />
            );
          }

          if (dataType === 'boolean') {
            return (
              <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <ConfigurationBooleanValueField
                  handleValueChange={handleValueChange}
                  formValue={row?.original.variableAttribute.value}
                  controlerValue={value || ''}
                />
              </Box>
            );
          }
          if (dataType === 'integer') {
            return (
              <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <ConfigurationIntegerValueField
                  handleValueChange={handleValueChange}
                  controlerValue={value}
                  minLimit={characteristics.minLimit}
                  maxLimit={characteristics.maxLimit}
                />
              </Box>
            );
          }
          if (dataType === 'decimal') {
            return (
              <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <ConfigurationDecimalValueField
                  handleValueChange={handleValueChange}
                  controlerValue={value}
                />
              </Box>
            );
          }
          if (dataType === 'string') {
            return (
              <ConfigurationStringValueFieldV21
                handleValueChange={handleValueChange}
                controlerValue={typeof value === 'string' ? value : ''}
                maxLimit={characteristics.maxLimit}
              />
            );
          }
          if (dataType === 'SequenceList') {
            if (!valuesList) {
              return (
                <ConfigurationStringValueFieldV21
                  handleValueChange={handleValueChange}
                  controlerValue={typeof value === 'string' ? value : ''}
                  maxLimit={0}
                />
              );
            }
            return (
              <ConfigurationCommaSeparatedMultiSelectV21
                valuesList={valuesList}
                controlerValue={typeof value === 'string' ? value : ''}
                handleValueChange={handleValueChange}
              />
            );
          }
          if (dataType === 'MemberList') {
            if (!valuesList) {
              return (
                <ConfigurationStringValueFieldV21
                  handleValueChange={handleValueChange}
                  controlerValue={typeof value === 'string' ? value : ''}
                  maxLimit={0}
                />
              );
            }
            return (
              <ConfigurationCommaSeparatedMultiSelectV21
                valuesList={valuesList}
                controlerValue={typeof value === 'string' ? value : ''}
                handleValueChange={handleValueChange}
              />
            );
          }
          if (dataType === 'OptionList') {
            if (!valuesList) {
              return (
                <ConfigurationStringValueFieldV21
                  handleValueChange={handleValueChange}
                  controlerValue={typeof value === 'string' ? value : ''}
                  maxLimit={0}
                />
              );
            }
            return (
              <ConfigurationOptionListValueFieldV21
                valuesList={valuesList}
                controlerValue={typeof value === 'string' ? value : ''}
                handleValueChange={handleValueChange}
              />
            );
          }
          if (dataType === 'dateTime') {
            return (
              <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <ConfigurationDateTimeValueField
                  handleValueChange={handleValueChange}
                  controlerValue={value || ''}
                />
              </Box>
            );
          }
          return <div>{row.original.variableAttribute.value || '-'}</div>;
        }}
        name={`setVariableData.${index}.attributeValueNew`}
      />
    );
  }

  return (
    <div>
      {capitalizeBooleanValue(row.original.variableAttribute.value) || '-'}
    </div>
  );
};

function capitalizeBooleanValue(value: string) {
  if (value === 'true' || value === 'false') {
    return upperFirst(value);
  }
  return value;
}
